import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { Grid } from "@mui/material";

import { useFlags } from "launchdarkly-react-client-sdk";

import Header from "components/Header/Header";
import PaymentHistory from "./PaymentHistory";
import Users from "./Users";
import Overview from "./Overview";
import CustomerProfile from "./CustomerProfile";
import PaymentDetails from "./PaymentDetails";
import { getValue } from "components/Screens/CommonUtils";

import PageLoader from "components/common/PageLoader";

import { GetCustomerDetails } from "store/actions/Customers/GetCustomerDetails";
import { ResetGetCustomerPaymentSummary } from "store/actions/Customers/GetCustomerPaymentSummary";
import { ResetGetCustomerPaymentById } from "store/actions/Customers/GetCustomerPaymentById";
import PageHeader from "components/Screens/PageHeader";
import UserList from "./UserList";
import Groups from "components/Screens/Groups";
import { hasPermission } from "hooks/usePermission";

const headersWithoutCustomerPaymentDetails = ["Profile", "Overview", "Payment History", "Users"];

function ComponentMapper({
  option,
  selectPageHeading,
  getSelectedPaymentId,
  customerId,
  selectedPaymentId,
  selectedPaymentHistoryRow,
  featureFlag,
}) {
  const handleNavigateToPaymentHistory = () => {
    selectPageHeading("Payment History");
  };

  const components = {
    Profile: <CustomerProfile />,
    Overview: <Overview onNavigateToPaymentHistory={handleNavigateToPaymentHistory} />,
    "Payment History": (
      <PaymentHistory selectPageHeading={selectPageHeading} getSelectedPaymentId={getSelectedPaymentId} />
    ),
    Users: featureFlag ? <UserList customerId={customerId} /> : <Users customerId={customerId} />,
    Groups: <Groups customerId={customerId} />,
    "Payment Details": (
      <PaymentDetails
        selectedPaymentId={selectedPaymentId}
        selectedPaymentHistoryRow={selectedPaymentHistoryRow}
        customerId={customerId}
      />
    ),
  };

  return components[option] || null;
}

function CustomerDetails() {
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const { state } = location;
  const [option, selectedOption] = useState(state?.activeTab || "Profile");
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [selectedPaymentHistoryRow, setSelectedPaymentHistoryRow] = useState(null);

  const customerDetails = useSelector((globalState) => globalState.GetCustomerDetails.customerDetails);
  const loadingCustomerDetails = useSelector((globalState) => globalState.GetCustomerDetails.loadingCustomerDetails);
  const loadingCustomerPaymentById = useSelector(
    (globalState) => globalState.GetCustomerPaymentById.loadingCustomerPaymentById
  );

  const customerId = customerDetails?.payload?.customerId;
  // The URL id is authoritative for what the tabs should render for. On an
  // in-place navigation to a different customer (e.g. a customerUser search
  // result deep-linking to the Users tab), Redux still briefly holds the
  // previous customer's payload - falling back to it here would pass the tabs
  // a stale/undefined customerId. `customerId` itself must stay derived from
  // Redux (see the GetCustomerDetails effect below, which compares params.id
  // against it to decide whether to refetch).
  const activeCustomerId = params.id || customerDetails?.payload?.customerId;

  const pageHeadings = hasPermission("groups.read")
    ? [...headersWithoutCustomerPaymentDetails, "Groups"]
    : headersWithoutCustomerPaymentDetails;

  const flags = useFlags();

  const selectPageHeading = (opt) => {
    if (opt != "Payment Details") {
      dispatch(ResetGetCustomerPaymentById({}));
    }
    selectedOption(opt);
  };

  const getSelectedPaymentId = (paymentId, paymentHistoryRow) => {
    if (paymentId != undefined || paymentId != null) {
      setSelectedPaymentId(paymentId);
      setSelectedPaymentHistoryRow(paymentHistoryRow);
    }
  };

  // React Router reuses this component instance across in-place navigations
  // (e.g. one customer-details page to another), so a fresh state.activeTab -
  // like the "Users" tab a customerUser search result asks for - must be
  // re-applied explicitly here. Keyed on location.key so it only fires on an
  // actual navigation, never on a re-render caused by the user manually
  // clicking a different tab (which never touches history/location).
  useEffect(() => {
    if (state?.activeTab) {
      selectedOption(state.activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (params.id) {
      dispatch(ResetGetCustomerPaymentSummary({}));
      if (!Object.keys(customerDetails).length || params.id !== customerId || state?.redirectFrom === "editCustomer") {
        dispatch(
          GetCustomerDetails({
            customerId: params.id,
          })
        );
      }
    }
  }, [params.id, state?.redirectFrom, customerId]);

  return (
    <>
      <Helmet>
        <title>{`Edge | Customers - ${getValue(customerDetails?.payload?.companyName)}`}</title>
      </Helmet>
      {(loadingCustomerDetails || loadingCustomerPaymentById) && <PageLoader />}
      <Grid container direction="column" rowSpacing={3}  sx={{ minWidth: 0 }}>
        <Header
          data={{
            heading: getValue(customerDetails?.payload?.companyName),
            subHeader: null,
            letterSpacing: "15px",
            profileWithIcon: null,
          }}
        />
        <PageHeader
          pageHeadings={pageHeadings}
          selectPageHeading={selectPageHeading}
          selectedOption={option}
        />

        <Grid container direction="row" sx={{ minWidth: 0, width: "100%" }}>
          <Grid size={12} sx={{ minWidth: 0 }}>
            <ComponentMapper
              option={option}
              selectPageHeading={selectPageHeading}
              getSelectedPaymentId={getSelectedPaymentId}
              customerId={activeCustomerId}
              selectedPaymentId={selectedPaymentId}
              selectedPaymentHistoryRow={selectedPaymentHistoryRow}
              featureFlag={flags?.teamManagement}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default CustomerDetails;
